import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatIDR } from "@/lib/format";
import { Button, Field, Select } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";
import { ArrowUpRight, ArrowDownRight, TrendingUp, PieChart, Calendar, Sparkles } from "lucide-react";

/**
 * Iter v34aw · Full-Bento — Laporan Laba/Rugi
 * Dark hero (net profit) + KPI mini + expense bento breakdown.
 */
export default function ProfitLoss() {
  const { t } = useLang();
  const today = new Date();
  const startDefault = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const endDefault = today.toISOString().slice(0, 10);
  const [start, setStart] = useState(startDefault);
  const [end, setEnd] = useState(endDefault);
  const [data, setData] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);

  const load = async () => {
    const r = await api.get(`/reports/profit-loss?start=${start}&end=${end}`);
    setData(r.data);
  };
  useEffect(() => { load(); api.get("/masters/categories").then((r) => setCategories(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const expenseEntries = data ? Object.entries(data.expense_by_category || {}).filter(([cat]) => !categoryFilter || cat === categoryFilter) : [];
  const filteredExpenseTotal = expenseEntries.reduce((s, [, amt]) => s + amt, 0);
  const filteredNetProfit = data ? (data.revenue - (categoryFilter ? filteredExpenseTotal : data.total_expense)) : 0;
  const netPositive = filteredNetProfit >= 0;
  const marginPct = data && data.revenue > 0 ? (filteredNetProfit / data.revenue) * 100 : 0;

  return (
    <div className="min-h-full bg-[#f6f5f0]">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 font-semibold">Keuangan · Laporan</div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-stone-900">{t("pnl")}</h1>
            <p className="text-stone-500 mt-1 text-sm">Periode dapat disesuaikan. Berbasis pembayaran yang sudah masuk dan pengeluaran berstatus lunas.</p>
          </div>
        </div>

        {/* Filter card */}
        <div className="rounded-2xl bg-white border border-stone-200/70 p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <Field label="Mulai"><DatePicker value={start} onChange={(e) => setStart(e.target.value)} data-testid="pnl-start" /></Field>
          <Field label="Akhir"><DatePicker value={end} onChange={(e) => setEnd(e.target.value)} data-testid="pnl-end" /></Field>
          <Field label="Kategori Pengeluaran">
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="pnl-category-filter">
              <option value="">Semua Kategori</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          <Button onClick={load} className="h-10" data-testid="pnl-load-btn"><Sparkles className="w-3.5 h-3.5"/> Hitung</Button>
        </div>

        {data && (
          <>
            {/* Bento hero */}
            <div className="grid grid-cols-12 gap-4">
              {/* Net profit hero card */}
              <div className={`col-span-12 md:col-span-6 rounded-3xl p-8 relative overflow-hidden text-white
                ${netPositive
                  ? "bg-gradient-to-br from-stone-900 via-stone-800 to-emerald-900"
                  : "bg-gradient-to-br from-stone-900 via-stone-800 to-red-900"}`}
                data-testid="pnl-net">
                <div className={`absolute -right-20 -top-20 w-72 h-72 rounded-full blur-3xl ${netPositive ? "bg-emerald-500/20" : "bg-red-500/20"}`}></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[11px] uppercase tracking-widest text-stone-400">
                      Laba Bersih{categoryFilter ? ` · ${categoryFilter}` : ""}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-stone-300 bg-white/5 px-2.5 py-1 rounded-full border border-white/10">
                      <Calendar className="w-3 h-3" />
                      {start} → {end}
                    </div>
                  </div>
                  <div className="font-display text-5xl md:text-6xl tracking-tight">{formatIDR(filteredNetProfit)}</div>
                  <div className="mt-3 text-sm text-stone-400 flex items-center gap-2">
                    <span className={netPositive ? "text-emerald-300" : "text-red-300"}>
                      {netPositive ? "▲" : "▼"} Margin {marginPct.toFixed(1)}%
                    </span>
                    <span className="text-stone-500">terhadap pendapatan</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
                    <div>
                      <div className="text-[10px] uppercase text-stone-400 tracking-wider flex items-center gap-1"><ArrowUpRight className="w-3 h-3"/> Revenue</div>
                      <div className="text-lg font-mono-num text-emerald-300 mt-1" data-testid="pnl-revenue">{formatIDR(data.revenue)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-stone-400 tracking-wider flex items-center gap-1"><ArrowDownRight className="w-3 h-3"/> Expense</div>
                      <div className="text-lg font-mono-num text-red-300 mt-1" data-testid="pnl-expense">{formatIDR(categoryFilter ? filteredExpenseTotal : data.total_expense)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right stats tiles */}
              <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5">
                  <ArrowUpRight className="w-5 h-5 text-emerald-700 mb-2"/>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Total Pendapatan</div>
                  <div className="font-display text-4xl mt-2 text-stone-900">{formatIDR(data.revenue)}</div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-100 p-4">
                  <ArrowDownRight className="w-4 h-4 text-red-700 mb-2"/>
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Total Pengeluaran</div>
                  <div className="font-mono-num text-xl mt-1 text-stone-900">{formatIDR(categoryFilter ? filteredExpenseTotal : data.total_expense)}</div>
                </div>
                <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
                  <TrendingUp className="w-4 h-4 text-blue-700 mb-2"/>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Margin</div>
                  <div className="font-mono-num text-xl mt-1 text-stone-900">{marginPct.toFixed(1)}%</div>
                </div>
              </div>
            </div>

            {/* Expense breakdown */}
            <div className="rounded-2xl bg-white border border-stone-200/70 p-6">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-5 h-5 text-stone-700"/>
                <h3 className="font-display text-xl tracking-tight text-stone-900">Pengeluaran per Kategori{categoryFilter ? ` · ${categoryFilter}` : ""}</h3>
              </div>
              <div className="space-y-3">
                {expenseEntries.length === 0 && <p className="text-sm text-stone-400 py-6 text-center">Tidak ada data pengeluaran pada periode ini.</p>}
                {expenseEntries.sort((a, b) => b[1] - a[1]).map(([cat, amt], i) => {
                  const totalRef = categoryFilter ? filteredExpenseTotal : data.total_expense;
                  const pct = totalRef ? (amt / totalRef) * 100 : 0;
                  const colors = ["from-blue-500 to-emerald-400", "from-emerald-500 to-teal-400", "from-amber-500 to-orange-400", "from-violet-500 to-fuchsia-400", "from-rose-500 to-pink-400"];
                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize text-stone-800 font-medium">{cat}</span>
                        <span className="font-mono-num text-stone-800">{formatIDR(amt)} <span className="text-stone-400">({pct.toFixed(1)}%)</span></span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${colors[i % colors.length]} rounded-full transition-all`} style={{ width: pct + "%" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
